import React from 'react';
import { cn } from 'shadcn-ui';
import { AssigneeWidgetLayoutProps, UserLogoSize } from './types';

const SIZE_CLASSES: Record<string, { container: string; text: string; badge: string; ring: string }> = {
  [UserLogoSize.SMALL]: {
    container: 'size-6',
    text: 'text-[9px]',
    badge: '-top-1.5 -right-1.5 min-h-3.5 min-w-3.5 text-[8px]',
    ring: '',
  },
  [UserLogoSize.MIDDLE]: {
    container: 'size-8',
    text: 'text-app-xs',
    badge: '-top-0.5 -right-0.5 min-h-4 min-w-4 text-[10px]',
    ring: 'ring-2 ring-white',
  },
  [UserLogoSize.BIG]: {
    container: 'size-8',
    text: 'text-app-xs',
    badge: '-top-0.5 -right-0.5 min-h-4 min-w-4 text-[10px]',
    ring: 'ring-2 ring-white',
  },
};

function getInitials(firstName: string, lastName: string): string {
  return `${firstName.trim().charAt(0)}${lastName.trim().charAt(0)}`;
}

function parseBadgeCount(badge?: string): number | undefined {
  if (!badge) {
    return undefined;
  }
  const parsed = parseInt(badge, 10);
  if (isNaN(parsed)) {
    return undefined;
  }
  return parsed;
}

export default function ShadcnAssigneeWidgetLayout(
  props: AssigneeWidgetLayoutProps,
) {
  const { profile, withName, onClick, badge, badgeTitle, avatarTitle } = props;
  const sizeKey = props.logoSize || UserLogoSize.BIG;
  const sizeConfig = SIZE_CLASSES[sizeKey] ?? SIZE_CLASSES[UserLogoSize.BIG];

  const fullName = profile.name.fullName;
  const initials = getInitials(profile.name.firstName, profile.name.lastName);
  const count = parseBadgeCount(badge);
  const title = avatarTitle || fullName;

  const avatarElement = (
    <div
      className={cn('relative inline-block group shrink-0', onClick && 'cursor-pointer')}
      title={count != null ? `${title} — ${count} ${badgeTitle || 'overdue'}` : title}
      onClick={!withName ? onClick : undefined}
    >
      {profile._avatarUrl ? (
        <img
          src={profile._avatarUrl.resized}
          alt={fullName}
          className={cn(
            'rounded-full object-cover transition-shadow',
            sizeConfig.container,
            sizeConfig.ring,
            onClick && 'group-hover:shadow-[0_2px_10px_rgba(0,0,0,0.5)]',
          )}
        />
      ) : (
        <div
          className={cn(
            'flex items-center justify-center rounded-full bg-gray-200 font-semibold text-gray-600 transition-shadow',
            sizeConfig.container,
            sizeConfig.text,
            sizeConfig.ring,
            onClick && 'group-hover:shadow-[0_2px_10px_rgba(0,0,0,0.5)]',
          )}
        >
          {initials}
        </div>
      )}
      {count != null && (
        <span
          className={cn(
            'absolute flex items-center justify-center rounded-full bg-rose-600 px-1 font-bold text-white ring-[1.5px] ring-white',
            sizeConfig.badge,
          )}
        >
          {count}
        </span>
      )}
    </div>
  );

  if (!withName) {
    return avatarElement;
  }

  const additionalInfo = React.Children.toArray(props.additionalInfo);

  return (
    <div
      className={cn('flex items-center gap-2', onClick && 'cursor-pointer')}
      onClick={onClick}
    >
      {avatarElement}
      <div className="min-w-0 overflow-hidden">
        <div className="font-bold text-app-body text-blue-900 leading-snug">
          {fullName}
        </div>
        {props.roles && props.roles.length > 0 && (
          <div className="text-app-xs text-gray-500 truncate">
            {props.roles.map((r) => r.roleName).join(', ')}
          </div>
        )}
        {additionalInfo.length > 0 && (
          <div>
            {additionalInfo.map((item, index) => (
              <div key={index} className="text-app-xs text-blue-900">
                {item}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
